import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { AxiosError } from 'axios';
import { AlertCircle, Loader2 } from 'lucide-react';
import {
  useAcceptInvitation,
  useAcceptInvitationAuthenticated,
  useInvitationInfo,
} from '@/hooks/useInvitations';
import { useAuth } from '@/hooks/useAuth';
import { HeldAccount, useHeldAccounts } from '@/hooks/useHeldAccounts';
import { MAX_ACCOUNTS, switchAccount } from '@/lib/activeAccount';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { OAuthButtons } from '@/features/oauthSso';
import { CredentialsSignInForm } from '@/features/auth/CredentialsSignInForm';
import { AuthShell } from './signup/AuthShell';

interface FormInputs {
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
}

interface ApiErrorResponse {
  error?: string;
  description?: string;
}

/** The add-account sign-in (docs/MultiAccountSessions.md §5), coming back to `returnTo`. */
function addAccountUrl(returnTo: string, email?: string): string {
  const params = new URLSearchParams({ add: '1', returnTo });
  if (email) params.set('email', email);
  return `/signin?${params.toString()}`;
}

export default function AcceptInvitation() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submitError, setSubmitError] = useState('');
  // A signed-in reader who chose "Create a new account" gets the signup form.
  const [creating, setCreating] = useState(false);
  const { data: held } = useHeldAccounts(!!user);

  const {
    data: invitationInfo,
    isLoading: isLoadingInfo,
    isError: isInfoError,
    error: infoError,
  } = useInvitationInfo(token);

  const acceptInvitationMutation = useAcceptInvitation();
  const acceptAuthenticatedMutation = useAcceptInvitationAuthenticated();

  const {
    register,
    handleSubmit: hookFormHandleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({ delayError: 750 });

  if (isLoadingInfo) {
    return (
      <AuthShell title="Loading invitation">
        <div className="p-6 flex flex-col items-center text-center">
          <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
        </div>
      </AuthShell>
    );
  }

  if (isInfoError || invitationInfo?.isExpired || invitationInfo?.isAccepted) {
    const errorMessage = invitationInfo?.isExpired
      ? 'This invitation has expired.'
      : invitationInfo?.isAccepted
        ? 'This invitation has already been accepted.'
        : infoError instanceof AxiosError && infoError.response?.status === 404
          ? 'This invitation could not be found. It may have been revoked.'
          : 'This invitation is invalid.';

    return (
      <AuthShell title="Invitation problem">
        <div className="p-6 flex flex-col items-center text-center">
          <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center text-destructive mb-4">
            <AlertCircle className="w-5 h-5" />
          </div>
          <p className="text-sm text-foreground mb-2">{errorMessage}</p>
          <p className="text-xs text-muted-foreground mb-4">
            Contact your administrator if you believe this is an error.
          </p>
          <Button onClick={() => { void navigate('/signin'); }} className="w-full">
            Go to sign in
          </Button>
        </div>
      </AuthShell>
    );
  }

  const onSubmit = (data: FormInputs): void => {
    if (!token) {
      setSubmitError('Invalid invitation token');
      return;
    }
    setSubmitError('');
    void acceptInvitationMutation
      .mutateAsync({
        token,
        firstName: data.firstName,
        lastName: data.lastName,
        password: data.password,
      })
      .then(
        () => {
          // Signed in to other accounts already: the new one is added beside them.
          void navigate(
            user
              ? addAccountUrl('/home', invitationInfo?.email)
              : '/signin?message=Account created successfully. Please sign in.',
          );
        },
        (error: unknown) => {
          if (error instanceof AxiosError) {
            const apiData = error.response?.data as ApiErrorResponse | undefined;
            setSubmitError(
              apiData?.description ??
                apiData?.error ??
                'Failed to accept invitation. Please try again.',
            );
          } else {
            setSubmitError('An unknown error occurred. Please try again.');
          }
        },
      );
  };

  // Already signed in: join an existing account to the org rather than creating a new one
  // (docs/MultiAccountSessions.md §7). This is the path back from `/signin?returnTo=…`, and
  // the browser may hold several accounts, so the reader chooses which one joins — the one
  // the invitation was sent to first. Accepting as whichever account a tab happened to be
  // on, whatever address the invitation went to, is how one person ended up with two.
  if (user && !creating) {
    const invitedEmail = invitationInfo?.email.toLowerCase();
    const isInvited = (account: HeldAccount) => account.email.toLowerCase() === invitedEmail;
    const signedIn: HeldAccount[] = held?.filter((account) => account.signedIn) ?? [
      { id: user.id, firstName: user.firstName, lastName: user.lastName, email: user.email, signedIn: true },
    ];
    const rank = (account: HeldAccount) => (isInvited(account) ? 0 : account.id === user.id ? 1 : 2);
    const choices = [...signedIn].sort((a, b) => rank(a) - rank(b));
    // The invited address belongs to an account this browser holds but has signed out.
    const invitedSignedOut = held?.find((account) => !account.signedIn && isInvited(account));
    const returnHere = `/accept-invitation/${token ?? ''}`;
    const canAdd = (held?.length ?? 1) < MAX_ACCOUNTS;

    const onAcceptAs = (account: HeldAccount): void => {
      if (!token) return;
      setSubmitError('');
      const another = account.id !== user.id;
      void acceptAuthenticatedMutation.mutateAsync({ token, account: another ? account.id : undefined }).then(
        () => {
          if (another) {
            switchAccount(account.id, '/home');
          } else {
            void navigate('/home');
          }
        },
        (error: unknown) => {
          if (error instanceof AxiosError) {
            const apiData = error.response?.data as ApiErrorResponse | undefined;
            setSubmitError(
              apiData?.description ?? apiData?.error ?? 'Failed to accept invitation. Please try again.',
            );
          } else {
            setSubmitError('An unknown error occurred. Please try again.');
          }
        },
      );
    };

    return (
      <AuthShell title="Accept invitation" width="md">
        <div className="px-6 py-4 border-b border-border bg-brand-soft/30">
          <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1">
            You've been invited to join
          </p>
          <p className="text-base font-semibold">{invitationInfo?.organizationName}</p>
          {invitationInfo?.inviterName ? (
            <p className="text-xs text-muted-foreground mt-1.5">
              Invited by <span className="text-foreground">{invitationInfo.inviterName}</span>
            </p>
          ) : null}
          <p className="text-xs text-muted-foreground">
            Sent to <span className="text-foreground font-mono">{invitationInfo?.email}</span>
          </p>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-muted-foreground">
            Choose the account that joins{' '}
            <span className="text-foreground font-medium">{invitationInfo?.organizationName}</span>.
          </p>

          <div className="space-y-2">
            {choices.map((account) => {
              const name = `${account.firstName} ${account.lastName}`.trim() || account.email;
              const pending =
                acceptAuthenticatedMutation.isPending &&
                (acceptAuthenticatedMutation.variables.account ?? user.id) === account.id;
              return (
                <div
                  key={account.id}
                  className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {account.email}
                      {isInvited(account) ? ' · invited address' : ''}
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant={account === choices[0] ? 'default' : 'outline'}
                    disabled={acceptAuthenticatedMutation.isPending}
                    onClick={() => {
                      onAcceptAs(account);
                    }}
                    aria-label={`Accept as ${account.email}`}
                  >
                    {pending ? 'Accepting…' : 'Accept'}
                  </Button>
                </div>
              );
            })}
          </div>

          {invitedSignedOut ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <span className="font-mono">{invitedSignedOut.email}</span> is signed out in this browser.{' '}
                <Link
                  to={addAccountUrl(returnHere, invitedSignedOut.email)}
                  className="text-primary hover:underline font-medium"
                >
                  Sign in to it
                </Link>{' '}
                to accept as that account.
              </AlertDescription>
            </Alert>
          ) : null}

          {submitError ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          ) : null}

          <div className="flex flex-col gap-1">
            {canAdd ? (
              <Button type="button" variant="ghost" className="w-full" asChild>
                <Link to={addAccountUrl(returnHere, choices.some(isInvited) ? undefined : invitationInfo?.email)}>
                  Use another account
                </Link>
              </Button>
            ) : null}
            {invitationInfo && !invitationInfo.accountExists && canAdd ? (
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setCreating(true);
                }}
              >
                Create a new account for {invitationInfo.email}
              </Button>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => {
                void navigate('/home');
              }}
            >
              Not now
            </Button>
          </div>
        </div>
      </AuthShell>
    );
  }

  // From the account chooser, sign-in adds an account; otherwise it is an ordinary sign-in.
  const signInUrl = user
    ? addAccountUrl(`/accept-invitation/${token ?? ''}`)
    : `/signin?returnTo=${encodeURIComponent(`/accept-invitation/${token ?? ''}`)}`;

  return (
    <AuthShell
      title="Accept invitation"
      width="md"
      footer={
        <>
          Already have an account?{' '}
          <Link to={signInUrl} className="text-primary hover:underline font-medium">
            Sign in
          </Link>
        </>
      }
    >
      {/* Invitation summary callout */}
      <div className="px-6 py-4 border-b border-border bg-brand-soft/30">
        <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1">
          You've been invited to join
        </p>
        <p className="text-base font-semibold">{invitationInfo?.organizationName}</p>
        {invitationInfo?.inviterName ? (
          <p className="text-xs text-muted-foreground mt-1.5">
            Invited by <span className="text-foreground">{invitationInfo.inviterName}</span>
          </p>
        ) : null}
        <p className="text-xs text-muted-foreground">
          Email: <span className="text-foreground font-mono">{invitationInfo?.email}</span>
        </p>
        {invitationInfo?.expiresAt ? (
          <p className="text-xs text-muted-foreground mt-1.5">
            Expires {new Date(invitationInfo.expiresAt).toLocaleDateString()}
          </p>
        ) : null}
      </div>

      {invitationInfo?.accountExists ? (
        /* The address already belongs to an account, so a signup here could only
           be rejected after the reader had filled it in. Offer the way in
           instead; the OAuth buttons below serve accounts that use a provider. */
        <CredentialsSignInForm
          fixedEmail={invitationInfo.email}
          submitLabel="Sign in &amp; accept invitation"
          intro={
            <p className="text-sm text-muted-foreground">
              This address already has an account. Sign in and we&apos;ll accept the
              invitation for you.
            </p>
          }
        />
      ) : (
      <form
        onSubmit={(e) => {
          void hookFormHandleSubmit(onSubmit)(e);
        }}
        className="p-6 space-y-4"
      >
        <fieldset
          disabled={acceptInvitationMutation.isPending}
          className="space-y-4 disabled:opacity-60"
        >
          <p className="text-sm text-muted-foreground">Create your account to get started.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="inv-firstName">First name</Label>
              <Input
                id="inv-firstName"
                type="text"
                autoComplete="given-name"
                aria-invalid={!!errors.firstName}
                {...register('firstName', { required: 'First name is required' })}
              />
              {errors.firstName ? (
                <p className="text-xs text-destructive">{errors.firstName.message}</p>
              ) : null}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="inv-lastName">Last name</Label>
              <Input
                id="inv-lastName"
                type="text"
                autoComplete="family-name"
                aria-invalid={!!errors.lastName}
                {...register('lastName', { required: 'Last name is required' })}
              />
              {errors.lastName ? (
                <p className="text-xs text-destructive">{errors.lastName.message}</p>
              ) : null}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="inv-password">Password</Label>
            <Input
              id="inv-password"
              type="password"
              autoComplete="new-password"
              aria-invalid={!!errors.password}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'Password must be at least 8 characters' },
              })}
            />
            {errors.password ? (
              <p className="text-xs text-destructive">{errors.password.message}</p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="inv-confirm">Confirm password</Label>
            <Input
              id="inv-confirm"
              type="password"
              autoComplete="new-password"
              aria-invalid={!!errors.confirmPassword}
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: (val: string, values) =>
                  values.password === val || 'Passwords do not match',
              })}
            />
            {errors.confirmPassword ? (
              <p className="text-xs text-destructive">{errors.confirmPassword.message}</p>
            ) : null}
          </div>

          {submitError ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {submitError}
                {/* This failure has a specific remedy: the account exists, so the
                    way in is to sign in and accept while authenticated. Saying so
                    here beats leaving the reader to spot the footer link. */}
                {/already exists/i.test(submitError) ? (
                  <>
                    {' '}
                    <Link to={signInUrl} className="underline font-medium">
                      Sign in instead
                    </Link>{' '}
                    and the invitation will be accepted for you.
                  </>
                ) : null}
              </AlertDescription>
            </Alert>
          ) : null}

          <Button
            type="submit"
            className="w-full"
            disabled={acceptInvitationMutation.isPending}
          >
            {acceptInvitationMutation.isPending
              ? 'Creating account…'
              : 'Accept invitation & create account'}
          </Button>
        </fieldset>
      </form>
      )}

      {/* Alternative: bind an existing Google / SSO account to this invitation.
          The flow joins the inviting org under the invited email — the linked
          identity may use a different provider email. Renders nothing when no
          providers are configured. */}
      {token ? (
        <div className="px-6 pb-6">
          <OAuthButtons invitationToken={token} returnTo="/home" withDivider />
        </div>
      ) : null}
    </AuthShell>
  );
}
